import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import accessImage from '../utils/accessImage';
import getFileFromFilename from '../utils/getDateFromFilename';

const MapContainer = styled.div`
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '100vh'};
  position: relative;
`;

/* Photo card shown dead-center over the map so it never runs off an edge.
   Portaled into the fullscreen element when the map is fullscreen so it stays
   visible there too. */
const CenteredCardOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.35);
`;

const OverlayCloseButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  z-index: 1001;
  border: none;
  background: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  line-height: 1;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
`;

const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const Input = styled.input`
  display: inline-block;
  margin-right: 10px;
`;

const Button = styled.button`
  display: inline-block;
  margin-bottom: 10px;
`;
const InfoWindowContent = styled.div`
  width: 350px;
  height: 350px;
  overflow: hidden;
  scroll: none;
`;

const Card = styled.div`
  background: white;
  overflow: hidden;
  margin: 0px;
  padding: 5px;
  width: 100%; 
  position: relative;
`;

const Image = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
`;

const Info = styled.div`
  padding: 5px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  color: #333;
`;

const Address = styled.p`
  color: #666;
  margin: 5px 0 5px;
  font-size: 12px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 3px 5px;
  font-size: 10px;
  color: ${props => props.textColor || 'white'};
  background: ${props => props.color || '#007bff'};
  border-radius: 3px;
  margin-right: 5px;
  position: absolute;
  top: ${props => (props.top ? props.top : '10px')};
  left: ${props => (props.right ? 'auto' : '10px')};
  right: ${props => (props.right ? '10px' : 'auto')};
`;

const getBadgeColors = (fullness) => {
  if (fullness <= 25) {
    return { background: '#28a745', text: '#ffffff' };
  } else if (fullness <= 50) {
    return { background: '#ffc107', text: '#ff0000' };
  } else if (fullness <= 74) {
    return { background: '#fd7e14', text: '#ffff00' };
  } else {
    return { background: '#dc3545', text: '#000000' };
  }
};

const MapComponent = ({ address, width, height, bounds, enableSearch = false, addresses = [], onInfoWindowClick, onBoundsChange, fitRequestKey }) => {
  const [currentAddress, setCurrentAddress] = useState(address);
  const [markers, setMarkers] = useState([]);
  const [map, setMap] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  // Google's fullscreen control expands an inner div of the map, not the
  // container we render into, so track it and portal the photo card there.
  const [fullscreenTarget, setFullscreenTarget] = useState(null);
  const addressCache = useRef({});
  const hasFitBoundsRef = useRef(false);
  const userInteractedRef = useRef(false);
  const latestAddressesRef = useRef(addresses);

  const apiKey = 'AIzaSyDUnATtTZiGS3Fcyh63-AUzg1qnNVry2U8';

  // Loads the Maps script once for the whole app and tolerates unmount/remount,
  // unlike <LoadScript>, which removes the script tag on unmount and hangs on
  // "Loading..." (error: "google api is already presented") when re-opened.
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    preventGoogleFontsLoading: true,
  });

  useEffect(() => {
    latestAddressesRef.current = addresses;
  }, [addresses]);

  useEffect(() => {
    const fetchCoordinates = async () => {
      const geocodedMarkers = addresses.map((location) => {
        if(location.lat && location.lng) {
          return location
        } else {
          return null;
        }
      });

      const validMarkers = geocodedMarkers.filter((marker) => marker !== null);
      setMarkers(validMarkers);
    };

    fetchCoordinates();
  }, [addresses, map]);

  const fitToMarkers = useCallback((mapInstance, addressList) => {
    const markerBounds = new window.google.maps.LatLngBounds();
    addressList.forEach((addr) => {
      if (addr.lat && addr.lng) {
        markerBounds.extend(new window.google.maps.LatLng(addr.lat, addr.lng));
      }
    });
    if (!markerBounds.isEmpty()) {
      mapInstance.fitBounds(markerBounds);
      hasFitBoundsRef.current = true;
    }
  }, []);

  // Backup fit: fires when map instance is ready, in case onLoad timing is unreliable
  useEffect(() => {
    if (!map || hasFitBoundsRef.current || !addresses.length) return;
    fitToMarkers(map, addresses);
  }, [map, addresses, fitToMarkers]);

  // The parent bumps fitRequestKey when the search text changes, so the map
  // re-centers on the new result set exactly as it does on open. This is an
  // explicit request, so it overrides any earlier manual pan/zoom.
  const lastFitRequestRef = useRef(fitRequestKey);
  useEffect(() => {
    if (!map || fitRequestKey === lastFitRequestRef.current) return;
    lastFitRequestRef.current = fitRequestKey;
    userInteractedRef.current = false;
    setActiveMarker(null);
    if (latestAddressesRef.current.length) {
      fitToMarkers(map, latestAddressesRef.current);
    }
  }, [map, fitRequestKey, fitToMarkers]);

  useEffect(() => {
    if (!map) return;
    const mapDiv = map.getDiv();
    const sync = () => {
      const el = document.fullscreenElement;
      setFullscreenTarget(el && mapDiv.contains(el) ? el : null);
    };
    document.addEventListener('fullscreenchange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      setFullscreenTarget(null);
    };
  }, [map]);

  // The map pane animates open (0.3s flex transition), so the map div can be
  // nearly zero width when onLoad fires and fitBounds then computes a far
  // zoomed-out view. Re-fit after the container size settles (debounced past
  // the transition), until the user takes control of the viewport themselves.
  const refitTimeoutRef = useRef(null);
  useEffect(() => {
    if (!map) return;
    const div = map.getDiv();
    const markInteracted = () => {
      userInteractedRef.current = true;
    };
    div.addEventListener('pointerdown', markInteracted);
    div.addEventListener('wheel', markInteracted, { passive: true });
    const observer = new ResizeObserver(() => {
      if (userInteractedRef.current) return;
      if (refitTimeoutRef.current) {
        clearTimeout(refitTimeoutRef.current);
      }
      // Wait for the flex transition to finish and the Maps API to register
      // the final container size; fitting mid-animation locks in a zoom
      // computed against a narrower pane.
      refitTimeoutRef.current = setTimeout(() => {
        if (userInteractedRef.current) return;
        window.google.maps.event.trigger(map, 'resize');
        if (latestAddressesRef.current.length) {
          fitToMarkers(map, latestAddressesRef.current);
        }
      }, 150);
    });
    observer.observe(div);
    return () => {
      observer.disconnect();
      if (refitTimeoutRef.current) {
        clearTimeout(refitTimeoutRef.current);
      }
      div.removeEventListener('pointerdown', markInteracted);
      div.removeEventListener('wheel', markInteracted);
    };
  }, [map, fitToMarkers]);

  const handleSearch = useCallback(async () => {
    if (currentAddress) {
      try {
        if (addressCache.current[currentAddress]) {
          // Si la dirección ya está en la cache, usarla
          const { lat, lng } = addressCache.current[currentAddress];
          // setCenter({ lat, lng });
          return;
        }

        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(currentAddress)}&key=${apiKey}`
        );
        const data = await response.json();
        if (data.results.length > 0) {
          const { lat, lng } = data.results[0].geometry.location;
          // setCenter({ lat, lng });
          addressCache.current[currentAddress] = { lat, lng }; // Guardar en cache
        }
      } catch (error) {
        console.error('Error fetching coordinates for address:', error);
      }
    }
  }, [currentAddress, apiKey]);

  const handleActiveMarker = (marker) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    if (marker === activeMarker) {
      return;
    }
    setActiveMarker(marker);
  };

  const handleMouseOut = () => {
    const timeout = setTimeout(() => {
      setActiveMarker(null);
    }, 1000);
    setHoverTimeout(timeout);
  };

  const boundsChangeTimeoutRef = useRef(null)
  const handleBoundsChanged = () => {
    if (!map) return;

    // Clear any existing timeout to debounce the event
    if (boundsChangeTimeoutRef.current) {
      clearTimeout(boundsChangeTimeoutRef.current);
    }

    // Set a new timeout to avoid triggering the event too frequently
    boundsChangeTimeoutRef.current = setTimeout(() => {
      const bounds = map.getBounds();
      if (bounds) {
        // // Call the parent component callback with the bounds data
        if (onBoundsChange) {
          onBoundsChange(bounds);
        }
      }
    }, 1000); // 1000ms debounce
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (boundsChangeTimeoutRef.current) {
        clearTimeout(boundsChangeTimeoutRef.current);
      }
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  return (
    <div>
      {enableSearch && (
        <SearchContainer>
          <Input
            type="text"
            value={currentAddress}
            onChange={(e) => setCurrentAddress(e.target.value)}
            placeholder="Ingresa una dirección"
          />
          <Button onClick={handleSearch}>Buscar</Button>
        </SearchContainer>
      )}
      <MapContainer width={width} height={height}>
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            onBoundsChanged={handleBoundsChanged}
            onLoad={(mapInstance) => {
              setMap(mapInstance);
              fitToMarkers(mapInstance, addresses);
            }}
            onUnmount={() => {
              hasFitBoundsRef.current = false;
              setMap(null);
              setActiveMarker(null);
              if (boundsChangeTimeoutRef.current) {
                clearTimeout(boundsChangeTimeoutRef.current);
              }
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
              }
            }}
          >
            {markers.map((marker, index) => (
              <Marker
                key={index}
                position={{ lat: marker.lat, lng: marker.lng }}
                label={marker.id}
                onClick={() => handleActiveMarker(marker.id)}
              />
            ))}
            {(() => {
              const active = markers.find((marker) => marker.id === activeMarker);
              if (!active) return null;
              const { info } = active;
              const image = accessImage(info.thumbnail.Key);
              const { background, text } = getBadgeColors(info.fullness);
              // Normally rendered inside the map container. In fullscreen
              // mode the browser only shows descendants of the fullscreen
              // element, so portal the card into it.
              const card = (
                <CenteredCardOverlay>
                  <OverlayCloseButton onClick={() => setActiveMarker(null)}>×</OverlayCloseButton>
                  <InfoWindowContent onClick={() => onInfoWindowClick(info)}>
                    <Card>
                      <Image src={image} alt="container image not found" />
                      <Badge color="#ffc107">{`${getFileFromFilename(accessImage(info.thumbnail.Key))}`}</Badge>
                      <Badge color="#6c757d" top="230px">{info.cameraId}</Badge>
                      <Badge color={background} textColor={text} right>{info.fullness}%</Badge>
                      <Info>
                        <Title>{info.customerName || info.name || 'Customer Name'}</Title>
                        <Address>{info.address || 'Address'}, {info.city}, {info.state}, {info.zip}</Address>
                      </Info>
                    </Card>
                  </InfoWindowContent>
                </CenteredCardOverlay>
              );
              return fullscreenTarget ? createPortal(card, fullscreenTarget) : card;
            })()}
          </GoogleMap>
        )}
      </MapContainer>
    </div>
  );
};

export default MapComponent;
